import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Search, 
  Box, 
  Cable, 
  Network, 
  Server, 
  Building2, 
  MapPin,
  Clock,
  X,
  Loader2,
  ArrowUp,
  ArrowDown,
  CornerDownLeft
} from 'lucide-react';
import { 
  useGlobalSearch, 
  useRecentSearches, 
  useSaveRecentSearch,
  useClearRecentSearches,
  useDeleteRecentSearch,
  type GlobalSearchResult 
} from '@/hooks/useGlobalSearch';
import { cn } from '@/lib/utils';

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Configuration for result type styling
const TYPE_CONFIG: Record<GlobalSearchResult['result_type'], { 
  icon: typeof Box; 
  color: string; 
  label: string;
}> = {
  asset: { icon: Box, color: 'text-blue-500', label: 'Asset' },
  cable: { icon: Cable, color: 'text-purple-500', label: 'Cable' },
  network: { icon: Network, color: 'text-green-500', label: 'Network' },
  subnet: { icon: Server, color: 'text-orange-500', label: 'Subnet' },
  datacenter: { icon: Building2, color: 'text-cyan-500', label: 'Data Center' },
  site: { icon: MapPin, color: 'text-pink-500', label: 'Site' },
};

export function GlobalSearchDialog({ open, onOpenChange }: GlobalSearchDialogProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  
  const { data: results, isLoading, debouncedQuery } = useGlobalSearch(query);
  const { data: recentSearches } = useRecentSearches();
  const saveRecentSearch = useSaveRecentSearch();
  const clearRecentSearches = useClearRecentSearches();
  const deleteRecentSearch = useDeleteRecentSearch();
  
  // Determine what to show
  const showRecentSearches = query.length < 2 && recentSearches && recentSearches.length > 0;
  const showResults = query.length >= 2 && results && results.length > 0;
  const showNoResults = query.length >= 2 && !isLoading && results?.length === 0;
  const showTypingHint = query.length > 0 && query.length < 2;
  
  // Items for keyboard navigation
  const navigableItems = showRecentSearches 
    ? recentSearches.map(r => ({ type: 'recent' as const, ...r }))
    : showResults 
      ? results.map(r => ({ type: 'result' as const, ...r }))
      : [];
  
  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedQuery, showRecentSearches]);
  
  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);
  
  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && navigableItems.length > 0) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, navigableItems.length]);
  
  const handleSelect = useCallback((url: string, searchQuery?: string) => {
    if (searchQuery && results) {
      saveRecentSearch.mutate({ searchQuery, resultCount: results.length });
    }
    navigate(url);
    onOpenChange(false);
  }, [navigate, onOpenChange, results, saveRecentSearch]);
  
  const handleRecentSearchClick = useCallback((searchQuery: string) => {
    setQuery(searchQuery);
  }, []);
  
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (navigableItems.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, navigableItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        const item = navigableItems[selectedIndex];
        if (item) {
          if (item.type === 'recent') {
            handleRecentSearchClick(item.search_query);
          } else {
            handleSelect(item.url, debouncedQuery);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        onOpenChange(false);
        break;
    }
  }, [navigableItems, selectedIndex, handleSelect, handleRecentSearchClick, debouncedQuery, onOpenChange]);
  
  const getTypeConfig = (type: GlobalSearchResult['result_type']) => {
    return TYPE_CONFIG[type] || TYPE_CONFIG.asset;
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search className="h-5 w-5 text-muted-foreground shrink-0" />
          <Input
            ref={inputRef}
            placeholder="Search assets, cables, networks, data centers..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="border-0 shadow-none focus-visible:ring-0 text-base px-0"
            autoFocus
          />
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {query && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => setQuery('')} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Content Area */}
        <ScrollArea className="max-h-[400px]">
          <div ref={listRef} className="py-2">
            {/* Recent Searches */}
            {showRecentSearches && (
              <div>
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Recent Searches
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => clearRecentSearches.mutate()}
                  >
                    Clear all
                  </Button>
                </div>
                {recentSearches.map((recent, index) => (
                  <div
                    key={recent.id}
                    data-index={index}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2 cursor-pointer group",
                      selectedIndex === index ? "bg-accent" : "hover:bg-accent/50"
                    )}
                    onClick={() => handleRecentSearchClick(recent.search_query)}
                  >
                    <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">{recent.search_query}</span>
                    <Badge variant="secondary" className="text-xs">
                      {recent.result_count} results
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRecentSearch.mutate(recent.id);
                      }} aria-label="Close">
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Typing Hint */}
            {showTypingHint && (
              <div className="px-4 py-8 text-center text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Type at least 2 characters to search</p>
              </div>
            )}
            
            {/* No Results */}
            {showNoResults && (
              <div className="px-4 py-8 text-center text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No results found for "{debouncedQuery}"</p>
                <p className="text-xs mt-1">Try a different search term</p>
              </div>
            )}
            
            {/* Search Results */}
            {showResults && (
              <div>
                <div className="px-4 py-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {results.length} Result{results.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {results.map((result, index) => {
                  const config = getTypeConfig(result.result_type);
                  const Icon = config.icon;
                  
                  return (
                    <div
                      key={`${result.result_type}-${result.id}`}
                      data-index={index}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 cursor-pointer",
                        selectedIndex === index ? "bg-accent" : "hover:bg-accent/50"
                      )}
                      onClick={() => handleSelect(result.url, debouncedQuery)}
                    >
                      <div className={cn("p-2 rounded-md bg-muted shrink-0", config.color)}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{result.title}</div>
                        {result.subtitle && (
                          <div className="text-sm text-muted-foreground truncate">
                            {result.subtitle}
                          </div>
                        )}
                      </div>
                      <Badge variant="outline" className="shrink-0 text-xs">
                        {config.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Empty State - No query */}
            {!query && !showRecentSearches && (
              <div className="px-4 py-8 text-center text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Start typing to search across all data</p>
                <p className="text-xs mt-1">Assets, cables, networks, subnets, and more</p>
              </div>
            )}
          </div>
        </ScrollArea>
        
        {/* Keyboard Shortcuts Footer */}
        <div className="flex items-center gap-4 px-4 py-2 border-t bg-muted/30 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-muted border text-[10px]">
              <ArrowUp className="h-3 w-3 inline" />
            </kbd>
            <kbd className="px-1.5 py-0.5 rounded bg-muted border text-[10px]">
              <ArrowDown className="h-3 w-3 inline" />
            </kbd>
            <span>Navigate</span>
          </div>
          <div className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-muted border text-[10px]">
              <CornerDownLeft className="h-3 w-3 inline" />
            </kbd>
            <span>Select</span>
          </div>
          <div className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-muted border text-[10px]">Esc</kbd>
            <span>Close</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
